const BACKEND_URI = "";

import {
    ChatAppRequest,
    ChatAppResponse,
    ChatAppResponseOrError,
    Config,
    SimpleAPIResponse,
    PowerplantConfig,
    PowerplantConfigResponseV2,
    RiskAnalysisAppRequest,
    AskElisaBotAppRequest,
    GDPRResponse,
    ConversationSummary,
    StoredConversationMessage
} from "./models";

export class ApiError extends Error {
    status: number;
    body: string;

    constructor(status: number, body: string) {
        super(`API Error ${status}: ${body}`);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

export function getHeaders(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
}

export async function configApiV1(token: string): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/api/v1/config`, {
        method: "GET",
        headers: getHeaders(token)
    });
    console.log(response);
    if (!response.ok) {
        const body = await response.text();
        throw new ApiError(response.status, body);
    }
    return (await response.json()) as Config;
}

export async function askApi(request: ChatAppRequest, token: string): Promise<ChatAppResponse> {
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApiV1(request: ChatAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function chatAgentApiV1(request: ChatAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/chat_agent`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function rcaAgentApiV1(request: ChatAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/rca_agent`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function chatCondensedApiV1(request: ChatAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/chat_condensed`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}
export async function riskAnalysisApiV1(request: RiskAnalysisAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/risk_analysis`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function searchEventApiV1(request: ChatAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/search_event`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function askElisaBotApiV1(request: AskElisaBotAppRequest, token: string, correlation_id: string | null): Promise<Response> {
    const headers: Record<string, string> = {
        ...getHeaders(token),
        "Content-Type": "application/json"
    };
    if (correlation_id !== null) {
        headers["X-Correlation-ID"] = correlation_id;
    }
    return await fetch(`${BACKEND_URI}/api/v1/ask_elisabot`, {
        method: "POST",
        headers,
        body: JSON.stringify(request)
    });
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function uploadFileApi(request: FormData, token: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: getHeaders(token),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, token: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(token: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: getHeaders(token)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function loadPowerplantConfigV2(powerplant: string, token: string): Promise<PowerplantConfig> {
    const response = await fetch(`${BACKEND_URI}/api/v2/powerplant_config/${powerplant.toLowerCase()}`, {
        method: "GET",
        headers: { ...getHeaders(token), "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch powerplant ui configuration: ${response.statusText}`);
    }

    const powerplantConfigV2: PowerplantConfigResponseV2 = await response.json();

    return {
        id: powerplantConfigV2.id,
        name: powerplantConfigV2.displayName ?? powerplant,
        imageUrl: powerplantConfigV2.imageUrl,
        sharepointLibraryUrl: powerplantConfigV2.sharepointLibraryUrl ?? "",
        exampleQuestions: powerplantConfigV2.exampleQuestions ?? [],
        knowledgeSources: (powerplantConfigV2.knowledgeSources ?? []).map(
            source => source.knowledgeSource.toLowerCase() as PowerplantConfig["knowledgeSources"][number]
        ),
        uiFields: (powerplantConfigV2.uiFields ?? []).map(field => ({
            name: field.id,
            displayName: field.displayName,
            values: field.values
        }))
    };
}

export type FilterOptionsResponse = {
    available_values: Record<string, Record<string, number>>;
    total_documents: number;
};

export async function getFilterOptionsApi(powerplant: string, selectedFilters: Record<string, string[]>, token: string): Promise<FilterOptionsResponse> {
    const response = await fetch(`${BACKEND_URI}/api/v1/filter_options/${powerplant.toLowerCase()}`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({
            selected_filters: selectedFilters,
            context: { site: powerplant }
        })
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch filter options: ${response.statusText}`);
    }

    return (await response.json()) as FilterOptionsResponse;
}

export async function listConversationsApiV2(powerplant: string, token: string): Promise<ConversationSummary[]> {
    const response = await fetch(`${BACKEND_URI}/api/v2/conversations/${powerplant.toLowerCase()}`, {
        method: "GET",
        headers: getHeaders(token)
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.statusText}`);
    }

    const data: { conversations: ConversationSummary[] } = await response.json();
    return data.conversations;
}

export async function getConversationMessagesApiV2(powerplant: string, conversationId: string, token: string): Promise<StoredConversationMessage[]> {
    const response = await fetch(`${BACKEND_URI}/api/v2/conversations/${powerplant.toLowerCase()}/${conversationId}/messages`, {
        method: "GET",
        headers: getHeaders(token)
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch conversation messages: ${response.statusText}`);
    }

    const data: { messages: StoredConversationMessage[] } = await response.json();
    return data.messages;
}

export async function getGdprApiV1(token: string): Promise<GDPRResponse> {
    const response = await fetch(`${BACKEND_URI}/api/v1/get_gdpr`, {
        method: "GET",
        headers: getHeaders(token)
    });

    if (!response.ok) {
        const body = await response.text();
        throw new ApiError(response.status, body);
    }

    return (await response.json()) as GDPRResponse;
}

export type EnhancePromptRequest = {
    powerplant: string;
    rawPrompt: string;
};

export type EnhancePromptResponse = {
    enhanced_prompt: string;
};

export async function enhancePromptApi(request: EnhancePromptRequest, token: string): Promise<EnhancePromptResponse> {
    const response = await fetch(`${BACKEND_URI}/api/v1/prompt/enhance`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const body = await response.text();
        throw new ApiError(response.status, body);
    }

    return (await response.json()) as EnhancePromptResponse;
}

export type FeedbackQAPair = {
    user: string;
    agent: string;
};

export type FeedbackRequest = {
    category: string;
    user_id: string;
    conversation_id?: string;
    comments: string;
    wrong_answers?: FeedbackQAPair[] | null;
};

export async function submitFeedbackApi(feedback: FeedbackRequest, token: string, powerplant: string): Promise<void> {
    const response = await fetch(`${BACKEND_URI}/api/v1/feedback`, {
        method: "POST",
        headers: { ...getHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({
            feedback,
            context: { site: powerplant, overrides: { filter_powerplant: powerplant } }
        })
    });

    if (!response.ok) {
        const body = await response.text();
        throw new ApiError(response.status, body);
    }
}
